import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import { scheduledPublicationMessage, usePosts, useDeletePost, useUpdatePost, postsApi } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { Binoculars, Pencil, Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { openPreview } from '@/utils/desktopActions';
import { confirmDialog } from '@/lib/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { GetStartedChecklist } from '@/components/GetStartedChecklist';
import { useBaxterChanged } from '@/lib/baxterEffects';
import { useTurnHighlight } from '@/lib/useTurnHighlight';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sites/$siteSlug/posts/')({
  component: PostsPage,
});

function PostsPage() {
  const { siteSlug } = Route.useParams();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = usePosts(siteSlug, { page, per_page: 20 });
  const deletePost = useDeletePost(siteSlug);
  const updatePost = useUpdatePost(siteSlug);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const previewInFlight = useRef(false);
  // Baxter just wrote a post — flash whichever rows actually moved.
  const flash = useTurnHighlight(data?.data?.map((row) => ({ id: row.id, title: row.title || 'Untitled', status: row.status, date: new Date(row.created_at).toLocaleDateString() })), useBaxterChanged('posts'));

  const handleToggleStatus = async (postId: string, publish: boolean) => {
    try {
      await updatePost.mutateAsync({
        id: postId,
        data: { status: publish ? 'published' : 'draft' },
      });
      toast.success(publish ? 'Post published' : 'Post unpublished');
    } catch {
      toast.error('Failed to update post status');
    }
  };

  const handlePreview = async (postId: string) => {
    if (previewInFlight.current) return;
    previewInFlight.current = true;
    setPreviewingId(postId);
    try {
      await openPreview({ viaMint: true, render: async () => {
        const post = (await postsApi.get(siteSlug, postId)).data;
        if (!post) throw new Error('Post could not be loaded for preview');
        return postsApi.previewFull(siteSlug, {
          title: post.title, content: post.content, slug: post.slug,
          featured_image: post.featured_image, featured_image_alt: post.featured_image_alt,
          featured_image_caption: post.featured_image_caption,
        });
      } });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to open preview');
    } finally {
      previewInFlight.current = false;
      setPreviewingId(null);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (await confirmDialog(`Delete "${title}"?`, { destructive: true, confirmLabel: 'Delete post' })) {
      try {
        await deletePost.mutateAsync(id);
        toast.success('Post deleted');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete post');
      }
    }
  };

  return (
    <div className="max-w-[1500px]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Posts</h1>
        <Button asChild>
          <Link to="/sites/$siteSlug/posts/new" params={{ siteSlug }}>
            New Post
          </Link>
        </Button>
      </div>

      <GetStartedChecklist siteSlug={siteSlug} />

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-muted-foreground">Error loading posts</div>
      ) : !data?.data ? (
        <div className="text-center py-12 text-muted-foreground">
          Error: {(data as unknown as { error?: string })?.error || 'Failed to load posts'}
        </div>
      ) : data.data.length === 0 ? (
        <div className="text-center py-16 bg-muted/40 rounded-lg border">
          <p className="text-lg font-medium">Nothing published yet</p>
          <p className="text-muted-foreground mt-1 text-sm">
            Your first post is a blank page waiting for a good idea.
          </p>
          <Button asChild className="mt-4">
            <Link to="/sites/$siteSlug/posts/new" params={{ siteSlug }}>
              Write your first post
            </Link>
          </Button>
        </div>
      ) : (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-[130px]">Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.data.map((post) => (
                  <TableRow
                    key={post.id}
                    data-changed={flash.has(post.id) ? 'true' : undefined}
                    className={cn(flash.has(post.id) && 'baxter-changed')}
                  >
                    <TableCell>
                      <Link
                        to="/sites/$siteSlug/posts/$postId"
                        params={{ siteSlug, postId: post.id }}
                        className="text-foreground font-medium hover:underline"
                      >
                        {post.title || 'Untitled'}
                      </Link>
                      {post.status === 'scheduled' && <p className="mt-1 text-xs text-muted-foreground">{scheduledPublicationMessage(post.status, data?.meta?.scheduled_publication, post.scheduled_for, post.id)}</p>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch checked={post.status === 'published'}
                          onCheckedChange={(checked) => void handleToggleStatus(post.id, checked)}
                          disabled={updatePost.isPending}
                          aria-label={post.status === 'published' ? 'Unpublish post' : 'Publish post'} />
                        <span className="text-sm text-muted-foreground">
                          {post.status === 'published' ? 'Published' : post.status === 'archived' ? 'Archived' : post.status === 'scheduled' ? 'Scheduled' : 'Draft'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(post.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon-sm"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="Preview post" title="Preview"
                          onClick={() => void handlePreview(post.id)}
                          disabled={previewingId !== null} aria-busy={previewingId === post.id}>
                          <Binoculars className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="Edit post"
                          title="Edit"
                          asChild
                        >
                          <Link
                            to="/sites/$siteSlug/posts/$postId"
                            params={{ siteSlug, postId: post.id }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          aria-label="Delete post"
                          title="Delete"
                          onClick={() => handleDelete(post.id, post.title)}
                          disabled={deletePost.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {data?.meta && data.meta.total_pages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="secondary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-muted-foreground">
                Page {page} of {data.meta.total_pages}
              </span>
              <Button
                variant="secondary"
                disabled={page >= data.meta.total_pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
